import type {CSSProperties} from 'react';
import {WEDGE_SHELL as B} from '@territory/tokens';
import {font, radius, space, weight} from '../scale.js';

export type WedgeAccountLook = 'solid' | 'signup-invite' | 'login-link' | 'password-toggle' | 'forgot';
/* Released account geometry and timing, off-scale. Other looks retain omitted native font properties. */
const SOLID_FONT = 15.5;
const SOLID_HEIGHT = 50;
const SOLID_PY = 15;
const SOLID_TRACKING = 0.3;
const SOLID_MS = 150;
const INVITE_PY = 7;
const INVITE_PX = 13;
const PULSE_SECONDS = 2.8;

export function wedgeAccountStyle(look: WedgeAccountLook, pending: boolean): CSSProperties {
  if (look === 'solid') return {
    position: 'relative', overflow: 'hidden', width: '100%', padding: `${SOLID_PY}px ${space[5]}px`,
    borderRadius: radius.xl, border: 0, background: B.accountGradient, color: B.accountInk,
    fontSize: SOLID_FONT, fontWeight: weight.bold, letterSpacing: SOLID_TRACKING,
    cursor: pending ? 'default' : 'pointer', opacity: pending ? 0.9 : 1,
    minHeight: SOLID_HEIGHT, fontFamily: 'inherit', boxShadow: B.accountShadow,
    transition: `transform ${SOLID_MS}ms ${B.ease}`,
  };
  const base: CSSProperties = {background: 'none', border: 0, color: B.textSec, cursor: pending && look === 'forgot' ? 'default' : 'pointer', fontFamily: 'inherit'};
  if (look === 'password-toggle') return {...base, fontSize: font.footnote, fontWeight: weight.semibold, padding: space[1]};
  if (look === 'forgot') return {...base, padding: 0};
  return {...base, color: look === 'signup-invite' ? B.signupInk : B.textSec, padding: look === 'signup-invite' ? `${INVITE_PY}px ${INVITE_PX}px` : 0, borderRadius: radius.pill, fontWeight: weight.bold};
}

const root = '[data-rv-button][data-rv-appearance="wedge-account"]';
const solid = `${root}[data-rv-account-look="solid"]`;
const invite = `${root}[data-rv-account-look="signup-invite"]`;
export const WEDGE_ACCOUNT_BUTTON_CSS = `
@keyframes wedgeAmberPulse {
  0%, 100% { box-shadow: ${B.signupPulseShadow}; border-color: ${B.signupBorder}; }
  50% { box-shadow: ${B.signupPulsePeakShadow}; border-color: ${B.signupPulsePeakBorder}; }
}
${solid} { position: relative; overflow: hidden; border: 0 !important; background: ${B.accountGradient} !important; color: ${B.accountInk} !important; box-shadow: ${B.accountShadow} !important; }
${solid}:hover:not(:disabled) { background: ${B.accountHoverGradient} !important; transform: translateY(-1px); box-shadow: ${B.accountHoverShadow} !important; }
${solid}:active:not(:disabled) { transform: translateY(1px); }
${invite} { border: 1px solid ${B.signupBorder} !important; background: ${B.signupFill} !important; color: ${B.signupInk} !important; animation: wedgeAmberPulse ${PULSE_SECONDS}s ease-in-out infinite; }
${invite}:hover { filter: brightness(1.08); transform: translateY(-1px); }
@media (prefers-reduced-motion: reduce) { ${invite} { animation: none !important; } }
`;
